import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import jssVariables from "../../../jss_setup";
import { Formik, Form } from "formik";
import { Placeholder } from "rsuite";
import {
  StyleTypeContext,
  TranslationContext,
  countries,
} from "../../../umbilical_tools";
import { DatepickerField, SelectField } from "../../forms/inputGroup/helpers";
import { InputField } from "../../forms/inputGroup";
import { DateTime } from "luxon";
import { driver_age_range } from "../../MainSearchForm/carRental/helpers";
import { useSelector } from "react-redux";

function initialValues(date) {
  return {
    title: "",
    gender: "",
    date_of_birth: [date],
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    postal_code: "",
    country: "",
  };
}

function fieldCustomStyles(customStyles = {}) {
  return {
    label: {
      textTransform: "capitalize",
      ..._.get(customStyles, "inputGroup.label", {}),
    },
    input: {
      border: "1px solid lightgray",
      ..._.get(customStyles, "inputGroup.input", {}),
      smallscreen: {
        height: "3.2rem",
        ..._.get(customStyles, "inputGroup.input.smallscreen", {}),
      },
    },
  };
}

const travellersFormsStyles = createUseStyles({
  forms: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    height: "max-content",
    gridAutoFlow: "row",
    gridAutoRows: "max-content",
    ..._.get(props, "customStyles.forms", {}),
  }),
  form: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.form", {}),
  }),
  guestHeader: (props) => ({
    fontSize: "large",
    fontWeight: "bold",
    ..._.get(props, "customStyles.guestHeader", {}),
  }),
  requiredFieldsLabel: (props) => ({
    fontSize: "small",
    color: "lightgray",
    paddingTop: jssVariables.half_gap,
    paddingBottom: jssVariables.normal_gap,
    ..._.get(props, "customStyles.requiredFieldsLabel", {}),
  }),
  divider: (props) => ({
    height: `calc(${jssVariables.normal_gap} / 4)`,
    width: "100%",
    background: jssVariables.colors.base_color,
    margin: `calc(${jssVariables.normal_gap} * 1) 0`,
    ..._.get(props, "customStyles.divider", {}),
  }),
  col2: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.col2", {}),
  }),
  travellerForm: (props) => ({
    borderRadius: "10px",
    border: `2px solid ${jssVariables.colors.base_color}`,
    padding: jssVariables.normal_gap,
    ..._.get(props, "customStyles.travellerForm", {}),
  }),
  placeholdersContainers: (props) => ({
    padding: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.placeholdersContainers", {}),
  }),
  leaderExtraInfo: (props) => ({
    ..._.get(props, "customStyles.leaderExtraInfo", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    forms: (props) => ({
      gridAutoFlow: "unset",
      maxHeight: "70vh",
      ..._.get(props, "customStyles.forms.smallscreen", {}),
    }),
    form: (props) => ({
      display: "grid",
      gridGap: jssVariables.normal_gap,
      gridTemplateColumns: "1fr 1fr",
      ..._.get(props, "customStyles.form.smallscreen", {}),
    }),
    divider: (props) => ({
      display: "none",
      ..._.get(props, "customStyles.divider.smallscreen", {}),
    }),
    travellerForm: (props) => ({
      border: `2px solid ${jssVariables.colors.base_color}`,
      padding: jssVariables.normal_gap,
      borderRadius: "10px",
      width: "45rem",
      justifySelf: "center",
      ..._.get(props, "customStyles.travellerForm.smallscreen", {}),
    }),
  },
});
const TravellersForms = ({ setDriver }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "TravellersForms",
  });
  const classes = travellersFormsStyles({
    customStyles,
  });
  const { t } = useContext(TranslationContext);
  const ghost = false;

  const search_form = useSelector((state) => state.CarRentalSearchForm);

  const max_date = DateTime.fromISO(search_form.drop_off_date)
    .minus({ years: search_form.driver_age })
    .toISODate();

  const min_date = DateTime.fromISO(max_date).minus({ years: 1 }).toISODate();

  return (
    <div className={classes.forms}>
      <div className={classes.travellerForm}>
        {ghost ? (
          <div className={classes.placeholdersContainers}>
            <Placeholder.Paragraph rows={1} active />
            <Placeholder.Paragraph rows={2} active />
            <Placeholder.Paragraph rows={2} active />
            <Placeholder.Paragraph rows={2} active />
            <Placeholder.Paragraph rows={2} active />
            <Placeholder.Paragraph rows={2} active />
            <Placeholder.Paragraph rows={2} active />
            <Placeholder.Paragraph rows={1} active />
          </div>
        ) : (
          <React.Fragment>
            <div className={classes.guestHeader}>
              <div>{t("driversInfo")}</div>
              <div className={classes.requiredFieldsLabel}>
                {t("requiredFields")}
              </div>
            </div>
            <Formik
              initialValues={initialValues(min_date)}
              validateOnChange={false}
              validateOnBlur={true}
              validate={async (values) => {
                setDriver(values);
              }}>
              <Form className={classes.form}>
                <div className={classes.col2}>
                  <SelectField
                    id="title"
                    label={t("title")}
                    name="title"
                    options={[
                      ["MR", "Mr."],
                      ["MRS", "Mrs."],
                      ["MS", "Ms."],
                    ]}
                    customStyles={fieldCustomStyles(customStyles)}
                    placeholder="----"
                  />
                  <SelectField
                    id="gender"
                    label={t("gender")}
                    name="gender"
                    options={[
                      ["male", "Male"],
                      ["female", "Female"],
                    ]}
                    customStyles={fieldCustomStyles(customStyles)}
                    placeholder="----"
                  />
                </div>
                <DatepickerField
                  id="date_of_birth"
                  label={t("dateOfBirth")}
                  name="date_of_birth"
                  dateFormat="MMM d, yyyy"
                  withPortal={true}
                  minDate={min_date}
                  maxDate={max_date}
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                  showYearDropdown={true}
                  yearDropdownItemsNum={driver_age_range[1]}
                />
                <InputField
                  id="first_name"
                  as="input"
                  label={t("firstName")}
                  name="first_name"
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                />
                <InputField
                  id="last_name"
                  as="input"
                  label={t("lastName")}
                  name="last_name"
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                />
                <InputField
                  id="email"
                  as="input"
                  label={t("email")}
                  name="email"
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                />
                <InputField
                  id="phone"
                  as="input"
                  label={t("phone")}
                  name="phone"
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="+XXXXXXXXXXXX"
                />
                <InputField
                  id="address"
                  as="input"
                  label={t("address")}
                  name="address"
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                />
                <InputField
                  id="city"
                  as="input"
                  label={t("city")}
                  name="city"
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                />
                <InputField
                  id="postal_code"
                  as="input"
                  label={t("postalCode")}
                  name="postal_code"
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                />
                <SelectField
                  id="country"
                  label={t("country")}
                  name="country"
                  options={Object.entries(countries).map(([key, value]) => [
                    key,
                    value,
                  ])}
                  customStyles={fieldCustomStyles(customStyles)}
                  placeholder="----"
                />
              </Form>
            </Formik>
          </React.Fragment>
        )}
      </div>
    </div>
  );
};
TravellersForms.defaultProps = {
  setDriver: {},
};
TravellersForms.propTypes = {
  setDriver: PropTypes.func.isRequired,
};
export default TravellersForms;
